import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiCheckCircle, FiSave, FiUpload, FiImage } from 'react-icons/fi';
import { api } from '../../utils/api';
import { triggerRefresh, REFRESH_EVENTS } from '../../utils/refreshEvents';
import './AdminServices.css';

const AdminServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'strategie',
    features: [],
    image: '',
    active: true,
    order: 0
  });
  const [newFeature, setNewFeature] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const res = await api.getServices();
      setServices(res.data);
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await api.updateService(editingService._id, formData);
      } else {
        await api.createService(formData);
      }
      fetchServices();
      resetForm();
      // Déclencher le rafraîchissement sur les pages publiques
      triggerRefresh(REFRESH_EVENTS.SERVICES);
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      title: service.title,
      description: service.description,
      category: service.category,
      features: service.features || [],
      image: service.image || '',
      active: service.active,
      order: service.order || 0
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce service ?')) {
      try {
        await api.deleteService(id);
        fetchServices();
        // Déclencher le rafraîchissement sur les pages publiques
        triggerRefresh(REFRESH_EVENTS.SERVICES);
      } catch (error) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: 'strategie',
      features: [],
      image: '',
      active: true,
      order: 0
    });
    setEditingService(null);
    setShowForm(false);
    setNewFeature('');
  };

  const handleImageUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setUploadingImage(true);
      try {
        const uploadFormData = new FormData();
        uploadFormData.append('image', file);
        const res = await api.uploadImage(uploadFormData);
        setFormData(prevFormData => ({
          ...prevFormData,
          image: res.data.url,
          features: prevFormData.features || []
        }));
        alert('Image uploadée avec succès !');
      } catch (error) {
        alert('Erreur lors de l\'upload: ' + (error.response?.data?.message || error.message));
      } finally {
        setUploadingImage(false);
      }
    };
    input.click();
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prevFormData => ({
        ...prevFormData,
        features: [...(prevFormData.features || []), newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      features: (prevFormData.features || []).filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="loading-admin">
        <div className="loading-spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  return (
    <div className="admin-services">
      <motion.div
        className="admin-header-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2>Gestion des Services</h2>
        <motion.button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {showForm ? (
            <>
              <FiX />
              <span>Annuler</span>
            </>
          ) : (
            <>
              <FiPlus />
              <span>Ajouter un service</span>
            </>
          )}
        </motion.button>
      </motion.div>

      <AnimatePresence>
        {showForm && (
          <motion.form
            onSubmit={handleSubmit}
            className="service-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="form-header">
              <h3>{editingService ? 'Modifier le service' : 'Nouveau service'}</h3>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Titre *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Ex: Stratégie & Coaching"
                />
              </div>
              <div className="form-group">
                <label>Catégorie *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="strategie">Stratégie & Coaching</option>
                  <option value="communication">Communication & Création</option>
                  <option value="administratif">Assistance Administrative</option>
                  <option value="soutien">Soutien Entreprises</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows="3"
                placeholder="Description du service..."
              />
            </div>

            <div className="form-group">
              <label>Image</label>
              <div className="image-upload-section">
                {formData.image && (
                  <div className="image-preview-small">
                    <img src={formData.image} alt="Preview" />
                    <button
                      type="button"
                      onClick={() => setFormData(prevFormData => ({
                        ...prevFormData,
                        image: '',
                        features: prevFormData.features || []
                      }))}
                      className="remove-image-btn"
                    >
                      <FiX />
                    </button>
                  </div>
                )}
                <div className="image-upload-controls">
                  <motion.button
                    type="button"
                    onClick={handleImageUpload}
                    className="btn btn-secondary"
                    disabled={uploadingImage}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <FiUpload />
                    <span>{uploadingImage ? 'Upload...' : formData.image ? 'Changer l\'image' : 'Uploader une image'}</span>
                  </motion.button>
                  <input
                    type="text"
                    value={formData.image || ''}
                    onChange={(e) => setFormData(prevFormData => ({
                      ...prevFormData,
                      image: e.target.value,
                      features: prevFormData.features || []
                    }))}
                    placeholder="Ou entrer une URL d'image"
                    className="image-url-input"
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Prestations</label>
              <div className="features-input">
                <input
                  type="text"
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                  placeholder="Ajouter une prestation"
                />
                <motion.button
                  type="button"
                  onClick={addFeature}
                  className="btn btn-secondary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FiPlus />
                </motion.button>
              </div>
              {formData.features && formData.features.length > 0 && (
                <ul className="features-list">
                  {formData.features.map((feature, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                    >
                      <FiCheckCircle className="feature-check-icon" />
                      <span>{feature}</span>
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="remove-feature-btn"
                      >
                        <FiX />
                      </button>
                    </motion.li>
                  ))}
                </ul>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Ordre d'affichage</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                  min="0"
                />
              </div>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  <span>Service actif</span>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <motion.button
                type="submit"
                className="btn btn-primary"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiSave />
                <span>{editingService ? 'Modifier' : 'Créer'}</span>
              </motion.button>
              {editingService && (
                <motion.button
                  type="button"
                  onClick={resetForm}
                  className="btn btn-secondary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <FiX />
                  <span>Annuler</span>
                </motion.button>
              )}
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      <motion.div
        className="services-list"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {services.map((service, index) => (
          <motion.div
            key={service._id}
            className="service-item"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.05 }}
            whileHover={{ y: -4, boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)" }}
          >
            <div className="service-info">
              <h3>{service.title}</h3>
              <p className="service-category">{service.category}</p>
              <p>{service.description}</p>
              {service.features && service.features.length > 0 && (
                <ul>
                  {service.features.map((feature, i) => (
                    <li key={i}>
                      <FiCheckCircle className="feature-bullet" />
                      {feature}
                    </li>
                  ))}
                </ul>
              )}
              <span className={`status ${service.active ? 'active' : 'inactive'}`}>
                {service.active ? 'Actif' : 'Inactif'}
              </span>
            </div>
            <div className="service-actions">
              <motion.button
                onClick={() => handleEdit(service)}
                className="btn btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiEdit2 />
                <span>Modifier</span>
              </motion.button>
              <motion.button
                onClick={() => handleDelete(service._id)}
                className="btn btn-danger"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiTrash2 />
                <span>Supprimer</span>
              </motion.button>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
};

export default AdminServices;
